import React, { useState, useEffect } from 'react';
import { AppLayout } from '../components/layout/AppLayout';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import Pricing from './Pricing';
import { User, CreditCard, Trash2, Save, Calendar, AlertCircle, CheckCircle, XCircle, UserPlus, Plus, Minus, Sparkles, ExternalLink } from 'lucide-react';
import { useUser, useAuth } from '../auth/ClerkWrapper';
import api from '../utils/api';

// Plan display names and pricing
const PLAN_INFO = {
    luna: { name: 'Luna', monthlyPrice: 0, annualPrice: 0, students: 3, workspaces: 1, addOnRate: null },
    mercury: { name: 'Mercury', monthlyPrice: 19, annualPrice: 193.80, students: 25, workspaces: 1, addOnRate: 0.80 },
    neptune: { name: 'Neptune', monthlyPrice: 39, annualPrice: 397.80, students: 60, workspaces: 2, addOnRate: 0.50 },
    jupiter: { name: 'Jupiter', monthlyPrice: 69, annualPrice: 703.80, students: 130, workspaces: 5, addOnRate: 0.30 },
    // Legacy plans
    free: { name: 'Free (Legacy)', monthlyPrice: 0, students: 3, workspaces: 1, addOnRate: null },
    starter: { name: 'Starter (Legacy)', monthlyPrice: 0, students: 3, workspaces: 1, addOnRate: null },
    pro: { name: 'Pro (Legacy)', monthlyPrice: 19, students: 100, workspaces: 3, addOnRate: 0.50 },
    enterprise: { name: 'Enterprise', monthlyPrice: 0, students: -1, workspaces: -1, addOnRate: null }
};

const Settings = ({ activeView, onViewChange, onLogout }) => {
    const { user } = useUser();
    const { getToken } = useAuth();
    const [activeTab, setActiveTab] = useState('profile');
    const [isSaving, setIsSaving] = useState(false);
    const [showPricing, setShowPricing] = useState(false);
    const [usage, setUsage] = useState(null);
    const [loadingUsage, setLoadingUsage] = useState(false);
    const [usageError, setUsageError] = useState(null);

    // Billing history state
    const [billingHistory, setBillingHistory] = useState([]);
    const [loadingBilling, setLoadingBilling] = useState(false);
    const [billingError, setBillingError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [billingSource, setBillingSource] = useState(null);

    // Subscription status state
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [loadingSubscription, setLoadingSubscription] = useState(false);
    const [reactivatingSubscription, setReactivatingSubscription] = useState(false);
    const [openingPortal, setOpeningPortal] = useState(false);

    // Add-on purchase state
    const [addonCount, setAddonCount] = useState(0);
    const [purchasingAddon, setPurchasingAddon] = useState(false);
    const [addonPricing, setAddonPricing] = useState(null);

    // Delete account state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingAccount, setDeletingAccount] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    // Profile settings state
    const [profileData, setProfileData] = useState({
        fullName: user?.fullName || '',
        email: user?.primaryEmailAddress?.emailAddress || '',
        institution: '',
        role: 'educator',
    });

    // Fetch usage data
    useEffect(() => {
        const fetchUsage = async () => {
            if (!user?.id) {
                return;
            }

            try {
                // Use the hook's token if possible, but here we access window.Clerk for now or need to pass getToken from props/hook
                // The component uses `useUser` but not `useAuth` which has `getToken`.
                const token = await window.Clerk?.session?.getToken();

                const response = await api.get(`/packages/usage/${user.id}`, {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });
                setUsage(response.data);
            } catch (error) {
                console.error('Error fetching usage:', error);
            }
        };

        fetchUsage();
    }, [user?.id, showPricing, activeTab]); // Refetch when returning from pricing or switching tabs

    // Fetch subscription status when billing tab is active
    useEffect(() => {
        const fetchSubscriptionStatus = async () => {
            if (activeTab !== 'billing') return;
            if (!user?.id) return;

            setLoadingSubscription(true);

            try {
                const token = await window.Clerk?.session?.getToken();
                const response = await api.get('/payments/subscription-status', {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });

                setSubscriptionStatus(response.data);
            } catch (error) {
                console.error('Error fetching subscription status:', error);
            } finally {
                setLoadingSubscription(false);
            }
        };

        fetchSubscriptionStatus();
    }, [activeTab, user?.id]);

    // Fetch add-on pricing when billing tab is active
    useEffect(() => {
        const fetchAddonPricing = async () => {
            if (activeTab !== 'billing') return;
            if (!user?.id) return;

            try {
                const token = await window.Clerk?.session?.getToken();
                const response = await api.get('/payments/addon-pricing', {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });

                setAddonPricing(response.data);
            } catch (error) {
                console.error('Error fetching addon pricing:', error);
            }
        };

        fetchAddonPricing();
    }, [activeTab, user?.id]);

    // Fetch billing history when billing tab is active
    useEffect(() => {
        const fetchBillingHistory = async () => {
            if (activeTab !== 'billing') return;

            // Don't refetch if we already have data and user hasn't changed
            // But DO refetch if we have no data yet
            if (billingHistory.length > 0 && !loadingBilling) return;

            if (!user?.id) {
                return;
            }

            setLoadingBilling(true);
            setBillingError(null);

            try {
                const token = await window.Clerk?.session?.getToken();

                const response = await api.get('/payments/billing-history', {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });

                setBillingHistory(response.data.invoices || []);
                setBillingSource(response.data.source);

                // If backend provides a message, show it as info
                if (response.data.message && response.data.invoices.length === 0) {
                    setBillingError(response.data.message);
                }

                setLastUpdated(new Date());
            } catch (error) {
                console.error('Error fetching billing history:', error);
                setBillingError(error.response?.data?.error || 'Failed to load billing history');
            } finally {
                setLoadingBilling(false);
            }
        };

        fetchBillingHistory();
    }, [activeTab, user?.id, billingHistory.length]);

    const handleSaveProfile = async () => {
        setIsSaving(true);
        await new Promise(resolve => setTimeout(resolve, 1000));
        setIsSaving(false);
        alert('Profile updated successfully!');
    };

    const handleReactivateSubscription = async () => {
        setReactivatingSubscription(true);
        try {
            const token = await window.Clerk?.session?.getToken();
            const response = await api.post('/payments/reactivate-subscription', {}, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });

            // Update local state
            setSubscriptionStatus(prev => ({
                ...prev,
                status: 'active',
                cancelAtPeriodEnd: false,
                cancelAt: null
            }));
        } catch (error) {
            console.error('Error reactivating subscription:', error);
            alert(error.response?.data?.error || 'Failed to reactivate subscription. Please try again.');
        } finally {
            setReactivatingSubscription(false);
        }
    };

    // Refetch subscription status from Stripe
    const refetchSubscriptionStatus = async () => {
        if (!user?.id) return;

        try {
            const token = await window.Clerk?.session?.getToken();
            const response = await api.get('/payments/subscription-status', {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            setSubscriptionStatus(response.data);
        } catch (error) {
            console.error('Error refetching subscription status:', error);
        }
    };

    // Refetch subscription status when window regains focus (user returns from Stripe portal)
    useEffect(() => {
        const handleFocus = () => {
            if (activeTab === 'billing') {
                refetchSubscriptionStatus();
            }
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [activeTab, user?.id]);

    // Handle opening Stripe Customer Portal for subscription management
    const handleManageSubscription = async () => {
        setOpeningPortal(true);
        try {
            const token = await window.Clerk?.session?.getToken();
            const response = await api.post('/payments/create-portal-session', {}, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });

            if (response.data.url) {
                // Open in new tab - status will refresh when user returns (focus event)
                window.open(response.data.url, '_blank');
            }
        } catch (error) {
            console.error('Error opening customer portal:', error);
            alert(error.response?.data?.error || 'Failed to open billing portal. Please try again.');
        } finally {
            setOpeningPortal(false);
        }
    };

    // Helper to format date nicely
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Handle add-on purchase
    const handlePurchaseAddOn = async () => {
        if (!addonPricing?.available || addonCount < 1) return;

        setPurchasingAddon(true);
        try {
            const token = await window.Clerk?.session?.getToken();
            const response = await api.post('/payments/purchase-addon', {
                studentCount: addonCount
            }, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });

            if (response.data.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            console.error('Error purchasing add-on:', error);
            alert(error.response?.data?.error || 'Failed to initiate add-on purchase. Please try again.');
        } finally {
            setPurchasingAddon(false);
        }
    };

    // Get plan display info
    const getPlanInfo = (packageType) => {
        return PLAN_INFO[packageType] || PLAN_INFO.luna;
    };

    // Handle account deletion
    const handleDeleteAccount = async () => {
        if (deleteConfirmText !== 'DELETE') return;

        setDeletingAccount(true);
        try {
            const token = await window.Clerk?.session?.getToken();
            await api.delete(`/packages/account/${user.id}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });

            // Sign out and redirect after successful deletion
            await window.Clerk?.signOut();
            window.location.href = '/';
        } catch (error) {
            console.error('Error deleting account:', error);
            alert(error.response?.data?.error || 'Failed to delete account. Please try again.');
            setDeletingAccount(false);
        }
    };

    const tabs = [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'billing', label: 'Billing', icon: CreditCard },
    ];

    // If showing pricing page, render only that
    if (showPricing) {
        return (
            <Pricing
                onSelectPlan={async (plan, billingInterval = 'monthly') => {
                    // Handle paid plans (Mercury, Neptune, Jupiter)
                    const paidPlans = ['Mercury', 'Neptune', 'Jupiter'];
                    if (paidPlans.includes(plan)) {
                        try {
                            const token = await getToken();
                            const response = await api.post('/payments/create-checkout-session', {
                                packageType: plan.toLowerCase(),
                                billingInterval: billingInterval
                            }, {
                                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                            });

                            if (response.data.url) {
                                window.location.href = response.data.url;
                            }
                        } catch (err) {
                            console.error('Payment error:', err);
                            alert(err.response?.data?.error || 'Failed to initiate payment. Please try again.');
                        }
                    } else if (plan === 'Institution') {
                        window.location.href = 'mailto:contact@grademind.ai?subject=Institution Plan Inquiry';
                    } else {
                        // Luna (free) or back
                        setShowPricing(false);
                    }
                }}
                onBack={() => setShowPricing(false)}
            />
        );
    }

    return (
        <AppLayout activeView={activeView} onViewChange={onViewChange} onLogout={onLogout}>
            <div className="space-y-8">
                {/* Header */}
                <div>
                    <h1 className="text-3xl font-bold text-black tracking-tight">Settings</h1>
                    <p className="text-zinc-500 mt-1">Manage your account settings and preferences.</p>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 bg-zinc-100 p-1 rounded-lg w-fit overflow-x-auto">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id
                                    ? 'bg-white text-black shadow-sm'
                                    : 'text-zinc-500 hover:text-black'
                                    }`}
                            >
                                <Icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Profile Tab */}
                {activeTab === 'profile' && (
                    <Card>
                        <CardHeader title="Profile Information" subtitle="Update your personal details" />
                        <div className="space-y-4">
                            <div className="flex items-center gap-4 pb-6 border-b border-zinc-100">
                                <img
                                    src={user?.imageUrl || `https://ui-avatars.com/api/?name=${user?.fullName || 'User'}&background=000000&color=fff`}
                                    alt="Profile"
                                    className="w-20 h-20 rounded-full"
                                />
                                <div>
                                    <h3 className="font-semibold text-black">{user?.fullName || 'User'}</h3>
                                    <p className="text-sm text-zinc-500">{user?.primaryEmailAddress?.emailAddress}</p>
                                </div>
                            </div>

                            <Input
                                label="Full Name"
                                value={profileData.fullName}
                                onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                                placeholder="John Doe"
                            />

                            <Input
                                label="Email Address"
                                type="email"
                                value={profileData.email}
                                disabled
                                className="bg-zinc-50"
                            />

                            <Input
                                label="Institution"
                                value={profileData.institution}
                                onChange={(e) => setProfileData({ ...profileData, institution: e.target.value })}
                                placeholder="University Name"
                            />

                            <div>
                                <label className="block text-sm font-medium text-black mb-1.5">Role</label>
                                <select
                                    value={profileData.role}
                                    onChange={(e) => setProfileData({ ...profileData, role: e.target.value })}
                                    className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm shadow-sm focus:outline-none focus:border-black focus:ring-2 focus:ring-black/5 hover:border-zinc-300 transition-all"
                                >
                                    <option value="educator">Educator</option>
                                    <option value="ta">Teaching Assistant</option>
                                    <option value="admin">Administrator</option>
                                </select>
                            </div>

                            <div className="pt-4">
                                <Button onClick={handleSaveProfile} isLoading={isSaving} icon={Save}>
                                    Save Changes
                                </Button>
                            </div>
                        </div>
                    </Card>
                )}


                {/* Billing Tab */}
                {activeTab === 'billing' && (
                    <div className="space-y-6">
                        <Card>
                            <CardHeader title="Current Plan" subtitle="Manage your subscription" />
                            <div className="space-y-4">
                                <div className="p-6 bg-gradient-to-br from-zinc-50 to-white border-2 border-zinc-200 rounded-2xl">
                                    {(() => {
                                        const planInfo = getPlanInfo(usage?.package || 'luna');
                                        const billingInterval = usage?.billingInterval;
                                        const price = billingInterval === 'annual'
                                            ? (planInfo.annualPrice / 12).toFixed(2)
                                            : planInfo.monthlyPrice;

                                        return (
                                            <>
                                                <div className="flex items-center justify-between mb-4">
                                                    <div>
                                                        <h3 className="text-2xl font-bold text-black">
                                                            {planInfo.name} Plan
                                                        </h3>
                                                        <p className="text-zinc-500">
                                                            {planInfo.monthlyPrice === 0 ? 'Free forever' : (
                                                                billingInterval === 'annual'
                                                                    ? `$${planInfo.annualPrice.toFixed(2)}/year (15% savings)`
                                                                    : `$${planInfo.monthlyPrice}/month`
                                                            )}
                                                        </p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-3xl font-bold text-black">
                                                            ${price}
                                                        </p>
                                                        <p className="text-sm text-zinc-500">/month</p>
                                                    </div>
                                                </div>
                                            </>
                                        );
                                    })()}

                                    {/* Subscription Status */}
                                    {loadingSubscription ? (
                                        <div className="p-4 text-center text-zinc-500">Loading subscription status...</div>
                                    ) : subscriptionStatus?.hasSubscription && (
                                        <div className="mb-4 p-4 bg-white border border-zinc-200 rounded-xl">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    {subscriptionStatus.status === 'active' && !subscriptionStatus.cancelAtPeriodEnd && (
                                                        <div className="flex items-center gap-2">
                                                            <CheckCircle className="w-5 h-5 text-green-600" />
                                                            <span className="text-sm font-medium text-green-700">Active</span>
                                                        </div>
                                                    )}
                                                    {(subscriptionStatus.status === 'canceling' || subscriptionStatus.cancelAtPeriodEnd) && (
                                                        <div className="flex items-center gap-2">
                                                            <AlertCircle className="w-5 h-5 text-amber-600" />
                                                            <span className="text-sm font-medium text-amber-700">Canceling</span>
                                                        </div>
                                                    )}
                                                    {subscriptionStatus.status === 'canceled' && (
                                                        <div className="flex items-center gap-2">
                                                            <XCircle className="w-5 h-5 text-red-600" />
                                                            <span className="text-sm font-medium text-red-700">Canceled</span>
                                                        </div>
                                                    )}
                                                    {subscriptionStatus.status === 'past_due' && (
                                                        <div className="flex items-center gap-2">
                                                            <AlertCircle className="w-5 h-5 text-red-600" />
                                                            <span className="text-sm font-medium text-red-700">Payment Past Due</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 text-sm text-zinc-600">
                                                    <Calendar className="w-4 h-4" />
                                                    {subscriptionStatus.cancelAtPeriodEnd ? (
                                                        <span>Access until: <span className="font-medium">{formatDate(subscriptionStatus.currentPeriodEnd)}</span></span>
                                                    ) : (
                                                        <span>Renews: <span className="font-medium">{formatDate(subscriptionStatus.currentPeriodEnd)}</span></span>
                                                    )}
                                                </div>
                                            </div>

                                            {subscriptionStatus.cancelAtPeriodEnd && (
                                                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                                    <p className="text-sm text-amber-800">
                                                        Your subscription is set to cancel. You'll have access until {formatDate(subscriptionStatus.currentPeriodEnd)}.
                                                    </p>
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        className="mt-2"
                                                        onClick={handleReactivateSubscription}
                                                        isLoading={reactivatingSubscription}
                                                    >
                                                        Reactivate Subscription
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Usage Stats - New format */}
                                    {loadingUsage && <div className="p-4 text-center text-zinc-500">Loading usage stats...</div>}
                                    {usageError && <div className="p-4 text-center text-red-500">{usageError}</div>}

                                    {!loadingUsage && !usageError && usage && (
                                        <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-zinc-200">
                                            <div className="bg-white rounded-lg p-4 border border-zinc-200">
                                                <p className="text-sm text-zinc-500 mb-1">Workspaces</p>
                                                <p className="text-2xl font-bold text-black">
                                                    {usage.usage.workspaces?.used || 0} / {usage.usage.workspaces?.limit === -1 ? '∞' : (usage.usage.workspaces?.limit || 1)}
                                                </p>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 border border-zinc-200">
                                                <p className="text-sm text-zinc-500 mb-1">Students This Period</p>
                                                <p className="text-2xl font-bold text-black">
                                                    {usage.usage.students?.usedThisPeriod || 0} / {usage.usage.students?.totalLimit === -1 ? '∞' : (usage.usage.students?.totalLimit || 3)}
                                                </p>
                                                {usage.usage.students?.addOnStudents > 0 && (
                                                    <p className="text-xs text-green-600 mt-1">
                                                        +{usage.usage.students.addOnStudents} add-on students
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-2 text-sm text-zinc-600 mt-4">
                                        {usage?.features?.map((feature, index) => (
                                            <p key={index}>✓ {feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                                        )) || (
                                                <>
                                                    <p>✓ Basic AI Grading</p>
                                                    <p>✓ {getPlanInfo(usage?.package || 'luna').students} Students</p>
                                                </>
                                            )}
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    {(!usage || ['luna', 'free', 'starter'].includes(usage?.package)) && (
                                        <>
                                            <Button
                                                variant="primary"
                                                onClick={() => setShowPricing(true)}
                                                className="flex-1"
                                            >
                                                Upgrade Plan
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                onClick={() => setShowPricing(true)}
                                            >
                                                View All Plans
                                            </Button>
                                        </>
                                    )}

                                    {usage?.package && ['mercury', 'neptune', 'jupiter', 'basic', 'pro'].includes(usage.package) && subscriptionStatus?.hasSubscription && !subscriptionStatus?.cancelAtPeriodEnd && (
                                        <>
                                            <Button
                                                variant="secondary"
                                                onClick={() => setShowPricing(true)}
                                                className="flex-1"
                                            >
                                                Change Plan
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                onClick={handleManageSubscription}
                                                isLoading={openingPortal}
                                                icon={ExternalLink}
                                            >
                                                Manage
                                            </Button>
                                        </>
                                    )}

                                    {usage?.package === 'enterprise' && (
                                        <Button
                                            variant="secondary"
                                            onClick={() => window.location.href = 'mailto:contact@grademind.ai?subject=Enterprise Plan Support'}
                                        >
                                            Contact Support
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Card>

                        {/* Unified Add-On Section */}
                        {addonPricing?.available && (
                            <div className="relative overflow-hidden rounded-2xl border border-zinc-200 bg-gradient-to-br from-white to-zinc-50 shadow-sm transition-all hover:shadow-md">
                                <div className="absolute top-0 right-0 -mt-8 -mr-8 w-32 h-32 bg-zinc-100 rounded-full blur-2xl"></div>

                                <div className="relative p-6 sm:p-8">
                                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6 mb-8">
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <div className="p-2 bg-zinc-100 rounded-lg text-black">
                                                    <Sparkles className="w-5 h-5" />
                                                </div>
                                                <h3 className="text-xl font-bold text-black">Boost Your Capacity</h3>
                                            </div>
                                            <p className="text-zinc-500 max-w-sm leading-relaxed">
                                                Running out of student slots? Instantly add more capacity to your current billing cycle.
                                            </p>
                                        </div>

                                        <div className="text-left sm:text-right bg-white p-4 rounded-xl border border-zinc-200 shadow-sm">
                                            <div className="flex items-baseline gap-1 sm:justify-end">
                                                <span className="text-3xl font-bold tracking-tight text-black">
                                                    ${(addonCount * addonPricing.addOnRate).toFixed(2)}
                                                </span>
                                                <span className="text-zinc-500 font-medium">USD</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 mt-1 text-xs text-zinc-600 font-medium">
                                                <Calendar className="w-3.5 h-3.5" />
                                                <span>Valid until {formatDate(addonPricing.periodEndsAt)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-xl p-2 border border-zinc-200 shadow-sm flex flex-col sm:flex-row items-center gap-4">
                                        <div className="flex-1 w-full flex items-center justify-between px-4 py-2">
                                            <span className="text-sm font-medium text-zinc-600 mr-4">Quantity</span>
                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={() => setAddonCount(Math.max(0, addonCount - 5))}
                                                    className="w-8 h-8 flex items-center justify-center rounded-full border border-zinc-200 text-zinc-500 hover:border-black hover:text-black hover:bg-zinc-50 transition-all disabled:opacity-30 disabled:hover:border-zinc-200 disabled:hover:text-zinc-500 disabled:hover:bg-transparent"
                                                    disabled={addonCount <= 0}
                                                >
                                                    <Minus className="w-4 h-4" />
                                                </button>

                                                <div className="w-16 text-center">
                                                    <input
                                                        type="number"
                                                        value={addonCount}
                                                        onChange={(e) => setAddonCount(Math.min(500, Math.max(0, parseInt(e.target.value) || 0)))}
                                                        className="w-full text-center font-bold text-lg text-black bg-transparent border-0 p-0 focus:ring-0"
                                                    />
                                                    <div className="text-[10px] text-zinc-400 uppercase tracking-wider font-semibold">Students</div>
                                                </div>

                                                <button
                                                    onClick={() => setAddonCount(Math.min(500, addonCount + 5))}
                                                    className="w-8 h-8 flex items-center justify-center rounded-full border border-zinc-200 text-zinc-500 hover:border-black hover:text-black hover:bg-zinc-50 transition-all disabled:opacity-30"
                                                    disabled={addonCount >= 500}
                                                >
                                                    <Plus className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="h-px w-full sm:h-12 sm:w-px bg-zinc-100"></div>

                                        <div className="w-full sm:w-auto p-2">
                                            <Button
                                                onClick={handlePurchaseAddOn}
                                                isLoading={purchasingAddon}
                                                disabled={addonCount === 0}
                                                className="w-full sm:w-auto !bg-black hover:!bg-zinc-800 text-white shadow-lg shadow-zinc-200/50 !py-2.5 !px-6 !rounded-lg disabled:opacity-50"
                                                icon={UserPlus}
                                            >
                                                Add Students
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Show message for Luna users who can't purchase add-ons */}
                        {addonPricing && !addonPricing.available && usage?.package === 'luna' && (
                            <Card>
                                <div className="p-6 text-center">
                                    <UserPlus className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                                    <h3 className="font-semibold text-zinc-900 mb-2">Add-On Students Not Available</h3>
                                    <p className="text-sm text-zinc-500 mb-4">
                                        The Luna plan doesn't support add-on students. Upgrade to Mercury or higher to purchase additional student capacity.
                                    </p>
                                    <Button variant="primary" onClick={() => setShowPricing(true)}>
                                        View Upgrade Options
                                    </Button>
                                </div>
                            </Card>
                        )}

                        <Card>
                            <div className="flex items-center justify-between p-5 border-b border-zinc-200">
                                <div>
                                    <h3 className="font-semibold text-black">Billing History</h3>
                                    <p className="text-sm text-zinc-500">
                                        View your past invoices
                                        {lastUpdated && (
                                            <span className="text-xs text-zinc-400 ml-2">
                                                • Last updated: {lastUpdated.toLocaleTimeString()}
                                                {billingSource && billingHistory.length > 0 && (
                                                    <span className="ml-1">({billingSource})</span>
                                                )}
                                            </span>
                                        )}
                                    </p>
                                </div>

                            </div>
                            <div className="p-5 space-y-4">
                                {loadingBilling && billingHistory.length === 0 && (
                                    <div className="text-center py-8 text-zinc-500">
                                        <p>Loading billing history...</p>
                                    </div>
                                )}

                                {billingError && (
                                    <div className={`p-4 border rounded-lg ${billingError.includes('Error') || billingError.includes('Unable')
                                        ? 'bg-red-50 border-red-200 text-red-700'
                                        : 'bg-blue-50 border-blue-200 text-blue-700'
                                        }`}>
                                        <p className="font-medium">
                                            {billingError.includes('Error') || billingError.includes('Unable')
                                                ? 'Error loading billing history'
                                                : 'Information'}
                                        </p>
                                        <p className="text-sm mt-1">{billingError}</p>
                                    </div>
                                )}

                                {!loadingBilling && !billingError && billingHistory.length === 0 && (
                                    <div className="text-center py-8 text-zinc-500">
                                        <p>No billing history available</p>
                                        <p className="text-sm mt-2">
                                            {usage?.package && ['basic', 'pro', 'enterprise'].includes(usage.package)
                                                ? 'Your invoices will appear here after your first billing cycle'
                                                : 'Your invoices will appear here once you upgrade to a paid plan'}
                                        </p>
                                        {usage?.package && ['basic', 'pro', 'enterprise'].includes(usage.package) && (
                                            <p className="text-xs text-zinc-400 mt-1">
                                                Current plan: {usage.package}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {!loadingBilling && !billingError && billingHistory.length > 0 && (
                                    <div className="space-y-3">
                                        {billingHistory.map((invoice) => (
                                            <div
                                                key={invoice.id}
                                                className="flex items-center justify-between p-4 bg-zinc-50 border border-zinc-200 rounded-lg hover:border-zinc-300 transition-colors"
                                            >
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-semibold text-black">
                                                            ${invoice.amount.toFixed(2)}
                                                        </span>
                                                        <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                                                            {invoice.status}
                                                        </span>
                                                    </div>
                                                    <div className="text-sm text-zinc-600">
                                                        {invoice.description} • {new Date(invoice.created).toLocaleDateString()}
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">

                                                    {invoice.hostedInvoiceUrl && (
                                                        <a
                                                            href={invoice.hostedInvoiceUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-sm text-blue-600 hover:text-blue-800 transition-colors px-3 py-1"
                                                        >
                                                            View
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Show loading indicator when refreshing existing data */}
                                {loadingBilling && billingHistory.length > 0 && (
                                    <div className="flex items-center justify-center py-4 text-zinc-500">
                                        <p>Refreshing...</p>
                                    </div>
                                )}
                            </div>
                        </Card>

                        <Card>
                            <CardHeader title="Danger Zone" subtitle="Irreversible actions" />
                            <div className="space-y-4">
                                <div className="p-4 border-2 border-red-200 bg-red-50 rounded-lg">
                                    <h4 className="font-semibold text-red-900 mb-2">Delete Account</h4>
                                    <p className="text-sm text-red-700 mb-4">
                                        Once you delete your account, there is no going back. All your data will be permanently deleted.
                                    </p>
                                    <Button variant="danger" icon={Trash2} onClick={() => setShowDeleteModal(true)}>
                                        Delete Account
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </div>
                )}
            </div>

            {/* Delete Account Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-red-100 rounded-full">
                                <Trash2 className="w-6 h-6 text-red-600" />
                            </div>
                            <h3 className="text-xl font-bold text-black">Delete Account</h3>
                        </div>
                        <p className="text-zinc-600 mb-4">
                            This action is <span className="font-semibold text-red-600">permanent and irreversible</span>.
                            All your workspaces, assignments, and data will be permanently deleted.
                        </p>
                        <p className="text-sm text-zinc-500 mb-4">
                            Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm:
                        </p>
                        <Input
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder="Type DELETE to confirm"
                            className="mb-4"
                        />
                        <div className="flex gap-3">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeleteConfirmText('');
                                }}
                                className="flex-1"
                                disabled={deletingAccount}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleDeleteAccount}
                                isLoading={deletingAccount}
                                disabled={deleteConfirmText !== 'DELETE'}
                                className="flex-1"
                            >
                                Delete My Account
                            </Button>
                        </div>
                    </div>
                </div>
            )}

        </AppLayout>
    );
};

export default Settings;
